// Preview of a project target (F05, RF-UI-10): the server decides the embed policy from the
// framing headers and the panel origin; the UI embeds live ONLY when `live_embed_allowed` is
// true, otherwise it offers the server's alternatives (new tab, download of the capture — an
// attachment, never rendered inline). Freshness, the candidate that was previewed versus the
// task's current candidate and the "URL filter, not network isolation" label are always shown.
import type { ReactNode } from "react";
import { useI18n } from "../../i18n/index.tsx";
import type { MessageKey } from "../../i18n/en.ts";
import type { PreviewTarget } from "../../api/types-wave3.ts";
import { Banner } from "../../components/Feedback.tsx";
import { ExternalLinkIcon } from "../../components/icons.tsx";
import { Hash, StatePill } from "../wave3/common.tsx";
import { PUBLISHED_APP_REASON } from "../microsystem/Publication.tsx";

const DISPLAY_VIEW: Record<PreviewTarget["display_state"], { tone: "success" | "warning" | "danger" | "info"; label: MessageKey }> = {
  preparing: { tone: "info", label: "preview.state.preparing" },
  available: { tone: "success", label: "preview.state.available" },
  stale: { tone: "warning", label: "preview.state.stale" },
  unavailable: { tone: "danger", label: "preview.state.unavailable" },
};

export function PreviewStateBadge({ target }: { target: PreviewTarget }) {
  const { t } = useI18n();
  const v = DISPLAY_VIEW[target.display_state];
  return <StatePill tone={v.tone} label={t(v.label)} testId="preview-state" />;
}

/** Candidate badge: which version the capture shows versus the task's current candidate. */
export function CandidateBadge({ target }: { target: PreviewTarget }) {
  const { t } = useI18n();
  const { previewed_candidate_hash: previewed, current_candidate_hash: current, matches } = target.version;
  return (
    <span className="row row--wrap" data-testid="candidate-badge" data-matches={matches === null ? "unknown" : String(matches)}>
      <span className="badge">
        {t("preview.candidate")}: <Hash value={previewed} />
      </span>
      {matches === false ? (
        <span className="badge badge--warning">
          {t("preview.currentCandidate")}: <Hash value={current} />
        </span>
      ) : matches === true ? (
        <span className="subtle">{t("preview.candidateMatches")}</span>
      ) : (
        <span className="subtle">{t("preview.candidateUnknown")}</span>
      )}
    </span>
  );
}

export function EgressLabel({ label }: { label: string }) {
  return (
    <span className="badge badge--warning" data-testid="egress-label">
      {label}
    </span>
  );
}

const staleReasonKey = (reason: string): MessageKey | null => {
  const map: Record<string, MessageKey> = {
    not_checked: "preview.stale.not_checked",
    candidate_differs: "preview.stale.candidate_differs",
    capture_without_candidate: "preview.stale.capture_without_candidate",
  };
  return map[reason] ?? null;
};

export function PreviewFacts({ target }: { target: PreviewTarget }) {
  const { t, formatDateTime } = useI18n();
  const reasonKey = target.stale_reason ? staleReasonKey(target.stale_reason) : null;
  return (
    <div className="preview-facts">
      <PreviewStateBadge target={target} />
      {target.stale ? (
        <span className="badge badge--warning" data-testid="stale-label">
          {t("preview.staleLabel")}: {reasonKey ? t(reasonKey) : target.stale_reason}
        </span>
      ) : null}
      <CandidateBadge target={target} />
      <EgressLabel label={target.egress_control_label} />
      <span className="subtle">{target.checked_at ? t("preview.checkedAt", { when: formatDateTime(target.checked_at) }) : t("preview.neverChecked")}</span>
    </div>
  );
}

/** Live iframe only when the server allows it; otherwise the listed alternatives. */
export function PreviewSurface({ target, extra }: { target: PreviewTarget; extra?: ReactNode }) {
  const { t, formatDateTime } = useI18n();
  if (target.live_embed_allowed) {
    return (
      <div className="preview-live" data-testid="preview-embed">
        <iframe
          className="preview-frame"
          src={target.url}
          title={t("preview.frameTitle", { url: target.url })}
          sandbox="allow-scripts allow-forms"
          referrerPolicy="no-referrer"
          loading="lazy"
        />
      </div>
    );
  }
  const policyKey: MessageKey = target.embed.reason === PUBLISHED_APP_REASON
    ? "publication.policy"
    : target.embed.policy === "capture_only" ? "preview.policy.capture_only" : target.embed.policy === "new_tab_only" ? "preview.policy.new_tab_only" : "preview.policy.embeddable_blocked";
  return (
    <div className="preview-alt" data-testid="preview-alternatives" data-policy={target.embed.policy}>
      <Banner tone="info" title={t("preview.noEmbedTitle")}>
        <p>{t(policyKey)}</p>
        <p className="subtle">
          {t("preview.embedReason")}: <span className="mono">{target.embed.reason}</span> · {t("preview.embedSource")}: <span className="mono">{target.embed.source}</span>
        </p>
      </Banner>
      <div className="row row--wrap">
        {target.alternatives.includes("open_in_new_tab") ? (
          <a className="btn btn--primary" href={target.url} target="_blank" rel="noopener noreferrer" data-testid="preview-open-tab">
            <ExternalLinkIcon size={16} />
            {t("preview.openNewTab")}
            <span className="sr-only">{t("common.opensNewTab")}</span>
          </a>
        ) : null}
        {target.alternatives.includes("view_capture") && target.capture?.download_url ? (
          <a className="btn" href={target.capture.download_url} download data-testid="preview-capture-link">
            {t("preview.downloadCapture")}
          </a>
        ) : null}
        {extra}
      </div>
      {target.capture ? (
        <p className="field-hint">
          {t("preview.captureFacts", { when: formatDateTime(target.capture.captured_at) })} <Hash value={target.capture.artifact_sha256} />
        </p>
      ) : (
        <p className="field-hint">{t("preview.noCapture")}</p>
      )}
      {target.alternatives.length === 0 ? <p className="field-hint">{t("preview.noAlternatives")}</p> : null}
    </div>
  );
}
